
import React from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";

// Define common areas for specific counties 
const countyAreas: Record<string, string[]> = {
  'dublin': ['North Dublin', 'South Dublin', 'Dublin City Centre', 'Dun Laoghaire', 'Blackrock', 'Howth', 'Malahide', 'Foxrock'],
  'cork': ['Cork City', 'North Cork', 'West Cork', 'East Cork', 'Ballincollig', 'Douglas', 'Mallow', 'Clonakilty'],
  'galway': ['Galway City', 'Connemara', 'East Galway', 'South Galway', 'Salthill', 'Oranmore', 'Athenry', 'Tuam'],
  'kerry': ['Tralee', 'Killarney', 'Dingle', 'Kenmare', 'Listowel', 'Castleisland', 'Cahersiveen'],
  'limerick': ['Limerick City', 'Newcastle West', 'Adare', 'Kilmallock', 'Castletroy', 'Dooradoyle', 'Raheen'],
  'mayo': ['Castlebar', 'Westport', 'Ballina', 'Claremorris', 'Ballinrobe', 'Belmullet'],
  'donegal': ['Letterkenny', 'Buncrana', 'Bundoran', 'Donegal Town', 'Ballybofey', 'Gweedore'],
  'wexford': ['Wexford Town', 'Enniscorthy', 'New Ross', 'Gorey', 'Rosslare', 'Bunclody'],
  'waterford': ['Waterford City', 'Dungarvan', 'Tramore', 'Lismore', 'Ardmore', 'Cappoquin'],
  'wicklow': ['Bray', 'Greystones', 'Wicklow Town', 'Arklow', 'Baltinglass', 'Rathnew']
};

// Fallback areas for counties not explicitly defined
const defaultAreas = ['North', 'South', 'East', 'West', 'Central'];

interface CountyAreasSectionProps {
  countyName: string;
  countySlug: string;
}

export function CountyAreasSection({ countyName, countySlug }: CountyAreasSectionProps) {
  // Get areas for this county, or use defaults
  const areas = countyAreas[countySlug.toLowerCase()] || 
    defaultAreas.map(direction => `${direction} ${countyName}`);
  
  return (
    <div className="my-12 bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <h2 className="text-2xl font-semibold mb-4 text-grandpal-green-dark">
        Browse Nursing Homes by Area in {countyName}
      </h2>
      
      <p className="text-grandpal-gray mb-6">
        Find nursing homes in specific areas of {countyName} to help narrow your search and find care closest to family and friends.
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {areas.map((area, index) => (
          <Link 
            key={index}
            to={`/nursing-homes/${countySlug}?area=${area.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center p-3 rounded-md hover:bg-grandpal-beige/30 transition-colors group"
          >
            <MapPin className="h-4 w-4 text-grandpal-green mr-2 group-hover:text-grandpal-green-dark transition-colors" />
            <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
              {area} Nursing Homes
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
